// The masthead, per design/Main.dc.html.
//
// Its OWN chrome rather than the shared rbnext Header, and that is the point:
// the first build of this page reused that header and the result looked nothing
// like the approved artboard. The artboard's masthead is navy, carries BOTH
// marks, ran a five-item nav in Live Sales / Markets / Reports / Barns /
// States (Markets since removed), and ends in "Are you a sale barn?" plus a gold Sign in.
//
// "Are you a sale barn?" is not decoration — the barn app is a separate product
// sold to barns, with its own front door at /my-barn/login, and this is the only
// place on the homepage that points at it.
//
// Plain <a> throughout: this document mounts no Router, so a <Link> would push
// history and render nothing.

import { useEffect, useRef, useState } from "react";

import { siteHomeHref } from "@/lib/siteHost";

import { FreeMonthStrip } from "./freeMonth";
import SignedInBadge from "./SignedInBadge";
import ThemeToggle from "@/brand/ThemeToggle";
import { WEIGHT_MAPS } from "../weight-map/weightMaps";
// Bigger type on phones for every public page (owner, 2026-09-14). Loaded
// here because every public page renders this masthead.
import "../mobileType.css";

interface NavChild {
  label: string;
  href: string;
}

interface NavItem {
  label: string;
  /** Absent on a group: the trigger opens the menu and navigates nowhere. */
  href?: string;
  /** A dropdown on a desktop, a tap-to-expand group in the phone panel. */
  children?: readonly NavChild[];
}

const NAV: readonly NavItem[] = [
  // ⚠️ "Home", NOT "Live Sales" (owner, 2026-10-02). The rename lands in the
  // same change that adds a "Cattle Ticker" item pointing at the lobby screen,
  // and that is not a coincidence: two items reading "Live Sales" and "Cattle
  // Ticker" invited the reader to guess which one was the board. "Home" says
  // what this item is — the front door — and leaves "Cattle Ticker" to mean
  // one thing.
  { label: "Home", href: "/" },
  // ⚠️ "Indexes" WAS HERE AND IS GONE AGAIN (owner, 2026-10-12: "remove
  // indexes from the main menu it doesn't go anywhere"). The index strip on
  // every page and the homepage cards both link to the indexes.
  // Leaderboard and the weight maps were reachable only from the gold picks
  // bar — and then only on a weekday an admin had scheduled them — and by
  // typed URL. Both are permanent nav items now (owner, 2026-09-24).
  // "Leaderboards" (owner, 2026-10-16: "Can Leaderboard be turned into
  // Leaderboards with the steer and heifer board as well as the calf tracker
  // under it?") — the three boards the leaderboard page draws, one group.
  // The heifer board had no link anywhere until this.
  {
    label: "Leaderboards",
    children: [
      { label: "Steer Leaderboard", href: "/leaderboard" },
      { label: "Heifer Leaderboard", href: "/leaderboard/heifers" },
      // "National Calf Leaderboard" since 2026-10-16 (owner: "change the name of the calf tracker"); the address stays.
      { label: "Calf Leaderboard", href: "/leaderboard/calves" },
    ],
  },
  // The one fed cattle page, USDA and the barns (owner, 2026-10-12: "Fed
  // Cattle needs to be a link on our top menu").
  { label: "Fed Cattle", href: "/fed-cattle" },
  // ⚠️ THE SEVEN MAPS COME FROM weightMaps.ts, WHICH IS THE ONE PLACE A CLASS
  // IS ADDED OR REMOVED. Retyping them here is how the nav ends up offering a
  // class the RPC's `p_band in (...)` guard rejects. An eighth class added
  // there appears in this menu with no edit to this file.
  // "Tools" (owner, 2026-10-08): the weight maps, and the two all-barn boards
  // that moved off the homepage so the homepage is the eleven-state indexes
  // and the live pieces, and these are every DVAuction barn on their own pages.
  {
    label: "Tools",
    children: [
      { label: "Weight Breaks", href: "/quotes-board" },
      { label: "Weekly Averages", href: "/weekly-averages" },
      ...WEIGHT_MAPS.map((m) => ({ label: m.name, href: m.path })),
    ],
  },
  // "Markets" (→ /market-report, choice-gated) removed on owner's call,
  // 2026-09-13. The rail's market cards already open the free market pages.
  { label: "Reports", href: "/reports" },
  { label: "Barns", href: "/barns" },
  { label: "States", href: "/states" },
  // The lobby board (src/lobby), on THIS site at /lobby — the same page
  // cattleticker.raisedbeef.com serves at "/".
  // ⚠️ NOT THE ABSOLUTE cattleticker.raisedbeef.com URL, which it was until
  // 2026-10-27. A sign-in is kept per address, so a signed-in visitor who
  // followed that link arrived signed out and met the Premium sign-up wall
  // (owner: "I am logged in but when i click on cattle Ticker … it acts like I
  // am not logged in"). The owner chose this fix over sharing one sign-in
  // across every raisedbeef.com address. cattleticker.raisedbeef.com stays the
  // barn TV's address; only this menu link moved.
  // ⚠️ DO NOT "FIX" THIS TO /, which is the homepage board, a different page.
  { label: "Cattle Ticker", href: "/lobby" },
  // Last, on the owner's call. /toppers shipped 2026-10-01 and until now was
  // reachable only by typed URL — a barn-marketing board no barn could find.
  { label: "Toppers", href: "/toppers" },
  // ⚠️ NO "Membership" LINK (owner, 2026-11-04: "we can get rid of the membership link in the
  // menu...i dont think we need it"). /membership itself stays: Join, the index locks and the
  // free-month strip all lead there. Added 2026-10-25.
  // Clara Light shipped 2026-10-02 (#2362, #2363) and had the same problem
  // /toppers had: a finished tool reachable only by typing the URL. Owner asked
  // for the entry by name.
  //
  // ⚠️ "Clara", AND IT IS NOW CORRECT — this entry said "Clara Light" until
  // 2026-10-03. Two things changed on the same day: the page became the front
  // door to BOTH products (the ask box is Clara Pro, the tiles are Clara
  // Light), and the owner retired the old Clara app, which had been occupying
  // /clara. The short label named neither product while the page was only
  // half the family; it names the family now.
  // ⚠️ CLARA IS OFF THE PUBLIC MENU FOR NOW. Owner, 2026-10-07: "put the
  // Clara menu link on the admin page for now until we have it done and remove
  // it from public view." The page still exists at /clara; the admin desk
  // (/manage/index-audit, Index tab) carries the link until the owner says the
  // product is done. Restore the entry here then — not before.
];

/** "/leaderboard/" and "/leaderboard" are the same page. "/" stays "/". */
function normalizePath(p: string): string {
  return p.length > 1 && p.endsWith("/") ? p.slice(0, -1) : p;
}

export default function TickerChrome({
  hero = false,
}: {
  /** The homepage's big-logo masthead. Interior ticker pages keep the compact
   *  one, where a tall header would cost the board a row. */
  hero?: boolean;
}) {
  // The phone menu. Closed on the server and on the first client render, so the
  // prerendered document and hydration agree; it only ever opens on a tap.
  const [menuOpen, setMenuOpen] = useState(false);
  // Which dropdown group is open (by label), and its phone-panel twin. TWO
  // values, not one: they are never on screen together, but sharing one
  // leaves the desktop panel hanging open behind a resize after a tap on the
  // phone accordion. Two groups since 2026-10-16 (Leaderboards, Tools), so a
  // label rather than a flag: opening one closes the other.
  const [openGroup, setOpenGroup] = useState<string | null>(null);
  const [openGroupMobile, setOpenGroupMobile] = useState<string | null>(null);

  // ⚠️ WHICH PAGE WE ARE ON IS READ AFTER MOUNT, NOT DURING RENDER. This
  // masthead is prerendered under Node, where there is no location, and a value
  // read during render would differ between the static document and hydration.
  // Null until the effect runs, so nothing is underlined for one frame — which
  // is the correct trade for a document that must hydrate cleanly.
  //
  // It replaces a hardcoded `current: true` on the Live Sales entry, which
  // underlined LIVE SALES on all ~19 pages that render this chrome, including
  // the one you had just navigated away from it to reach.
  const [path, setPath] = useState<string | null>(null);
  useEffect(() => setPath(normalizePath(window.location.pathname)), []);

  const isCurrent = (item: NavItem): boolean =>
    path !== null &&
    (item.href === path || (item.children?.some((c) => c.href === path) ?? false));

  // Escape and a click outside close the dropdown — copied from ToolLauncher,
  // which solved this on the same prerendered surface. Listeners go in an
  // effect and never at module scope: an effect does not run during a prerender.
  const groupRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const groupBtnRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  useEffect(() => {
    if (!openGroup) return;
    const label = openGroup;

    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      setOpenGroup(null);
      // Send focus back where it came from, or a keyboard user is dropped at
      // the top of the document with no idea what just happened.
      groupBtnRefs.current[label]?.focus();
    };
    const onPointerDown = (e: MouseEvent) => {
      if (!groupRefs.current[label]?.contains(e.target as Node)) setOpenGroup(null);
    };

    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onPointerDown);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onPointerDown);
    };
  }, [openGroup]);

  /** Closing the hamburger collapses the maps with it, so the next tap on the
   *  menu button opens the short list rather than whatever was left expanded. */
  const toggleMenu = () =>
    setMenuOpen((v) => {
      if (v) setOpenGroupMobile(null);
      return !v;
    });

  return (
    <>
    {/* ⚠️ NO "Driven by DVAuction and RaisedBeef" STRIP above the menu (owner, 2026-11-04: "remove the
        dvauction and raisedbeef logo at the top and remove the top dark menu"). The Cattle Market Central
        logo now says "Powered by DVAuction" itself. */}
    <header className="rbtx-chrome" data-hero={hero ? "1" : undefined}>
      <div className="rbtx-chrome-left">
        <a className="rbtx-marks" href={siteHomeHref()} aria-label="RaisedBeef, powered by DVAuction — home">
          {/* RaisedBeef leads, then "Powered by DVAuction" — the attribution
              for the auction data the whole page is built on, said in words
              rather than left to two logos side by side (owner, 2026-09-13). */}
          <img className="rbtx-mark-rb" src="/brand/rb-white.png" alt="RaisedBeef" />
          <span className="rbtx-marks-rule" aria-hidden="true" />
          {/* ⚠️ NO TAGLINE (owner, 2026-10-08: "just remove the text U.S.
              livestock market data and live auction transactions and
              proprietary indexes for now"). It lived here for a morning,
              from the design mock, in one line then three; it crowded the
              row on his screen and was hard to read. The mark and the
              DVAuction attribution stand alone again. */}
          <span className="rbtx-poweredby">
            <span className="rbtx-poweredby-l">Powered by</span>
            <img className="rbtx-mark-dv" src="/brand/dv-mark.png" alt="DVAuction" />
            {/* Cattle Market Central only ("driven by DVAuction and RaisedBeef", src/brand/): hidden on RaisedBeef by CSS. */}
            <span className="cmc-only rbtx-and">and</span>
            <img className="cmc-only rbtx-mark-rb2 cmc-keep" src="/brand/rb-white.png" alt="and RaisedBeef" />
          </span>
        </a>

        <nav className="rbtx-nav" aria-label="Main">
          {NAV.map((n) =>
            n.children ? (
              // Two groups (Leaderboards, Tools): the refs are keyed by label.
              <div className="rbtx-navgroup" key={n.label} ref={(el) => { groupRefs.current[n.label] = el; }}>
                {/* ⚠️ A DISCLOSURE, NOT role="menu". WAI's menu/menubar roles
                    are for application menus and promise arrow-key navigation
                    we do not implement; their own guidance for site navigation
                    is this pattern — a button carrying aria-expanded over a
                    plain group of links, which Tab already handles.
                    No aria-controls either: the panel does not exist while
                    closed, and aria-controls must point at a real element. */}
                <button
                  type="button"
                  ref={(el) => { groupBtnRefs.current[n.label] = el; }}
                  className="rbtx-navgroup-btn"
                  aria-expanded={openGroup === n.label}
                  // "true", not "page": the trigger is not the page you are on,
                  // it is the section containing it.
                  aria-current={isCurrent(n) ? "true" : undefined}
                  onClick={() => setOpenGroup((v) => (v === n.label ? null : n.label))}
                >
                  {n.label}
                  <span className="rbtx-navcaret" aria-hidden="true" />
                </button>
                {/* Rendered ONLY when open. Otherwise the prerendered document
                    of every public page carries seven more outbound links than
                    it shows. */}
                {openGroup === n.label && (
                  <div className="rbtx-navpanel" aria-label={n.label}>
                    {n.children.map((c) => (
                      <a
                        key={c.href}
                        href={c.href}
                        aria-current={path === c.href ? "page" : undefined}
                      >
                        {c.label}
                      </a>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <a key={n.label} href={n.href === "/" ? siteHomeHref() : n.href} aria-current={isCurrent(n) ? "page" : undefined}>
                {n.label}
              </a>
            ),
          )}
        </nav>
      </div>

      <div className="rbtx-chrome-right">
        {/* ⚠️ NO SEARCH BOX (owner, 2026-11-04: "lets remove the search barns field in the menu for
            now...i want to simplify"). It was MastheadSearch (2026-10-08); to bring it back, restore it
            from git history (git log -S MastheadSearch). Its styles (.rbtx-msearch) are still in
            tickerHome.css. */}
        <ThemeToggle />
        {/* The barn product's door. NOT /auth — that is Market Intelligence's,
            and the two are separate products that share one login. Sending a
            barn to the wrong one signs them up for the wrong thing. */}
        {/* ⚠️ THE "ARE YOU A SALE BARN?" LINK WAS HERE AND IS GONE
            (owner, 2026-09-13): "I just don't want the my barn showing up any
            longer for a RaisedBeef.com user."

            The barn product is sold to barns and reached through its own door;
            it is not a feature of the public market site and should not be
            advertised on it. A barn owner gets the link from us directly and
            from their own app.

            /my-barn/login is still ROUTED — this removes the signposting, not
            the door. */}
        {/* Lazy and effect-driven: see SignedInBadge's header. It must not
            pull the Supabase client into the prerendered document. */}
        {/* The email and the gold Sign in / Account button, together. */}
        <SignedInBadge />
        {/* ── THE PHONE MENU (owner, 2026-09-14: "there is no menu on mobile
            for other pages"). The desktop nav is hidden under 640px to fit the
            logo and Sign in on one row, and nothing replaced it — so a phone
            visitor could not reach Reports, Barns or States at all. */}
        <button
          type="button"
          className="rbtx-menubtn"
          aria-expanded={menuOpen}
          aria-controls="rbtx-mobilenav"
          aria-label={menuOpen ? "Close menu" : "Open menu"}
          onClick={toggleMenu}
        >
          <span className="rbtx-menubtn-bars" aria-hidden="true" data-open={menuOpen ? "1" : undefined}>
            <i />
            <i />
            <i />
          </span>
        </button>
      </div>
    </header>
    {/* The free month's days left and Subscribe, on every page (owner, 2026-11-03). Lazy; see ./freeMonth. */}
    <FreeMonthStrip />
    <nav id="rbtx-mobilenav" className="rbtx-mobilenav" aria-label="Main" hidden={!menuOpen}>
      {NAV.map((n) =>
        n.children ? (
          <div className="rbtx-mobilegroup" key={n.label}>
            <button
              type="button"
              className="rbtx-mobilegroup-btn"
              aria-expanded={openGroupMobile === n.label}
              aria-current={isCurrent(n) ? "true" : undefined}
              onClick={() => setOpenGroupMobile((v) => (v === n.label ? null : n.label))}
            >
              {n.label}
              <span className="rbtx-mobilecaret" aria-hidden="true" data-open={openGroupMobile === n.label ? "1" : undefined} />
            </button>
            {openGroupMobile === n.label &&
              n.children.map((c) => (
                <a
                  className="rbtx-mobilesub"
                  key={c.href}
                  href={c.href}
                  aria-current={path === c.href ? "page" : undefined}
                >
                  {c.label}
                </a>
              ))}
          </div>
        ) : (
          <a key={n.label} href={n.href === "/" ? siteHomeHref() : n.href} aria-current={isCurrent(n) ? "page" : undefined}>
            {n.label}
          </a>
        ),
      )}
      {/* On a phone the theme switch lives here, not on the logo's row (see cmc.fixes.css). */}
      <ThemeToggle variant="menu" />
    </nav>
    </>
  );
}
